/**
 * "Tag this drop" / "Tag this clip": one dropdown per brand facet, opened from the Upload drop the moment
 * its files start landing, from every clip's three-dot menu afterwards, and for a selection in Tagging.
 * A single-choice category (a person, a shape) is a select (No tag / known values / Add new…); every
 * other category is a MultiSelect of ticks (Dax, 2026-10-01). Opens on what every listed clip already
 * shares per facet, so a field left alone never touches an automatic or hand-applied tag; only what the
 * user ticks or unticks is sent, added to or taken off every clip, and a single-choice pick replaces
 * that facet on the clip. A new name joins its dropdown for next time. A drop of several video files
 * also carries the one question the drop needs answered while it lands: are these files one project?
 * Every way out of the modal answers it (No when untouched), so the upload is never left waiting.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { facetChanges, groupFacetValues, type UploadGrouping } from '@studio/contracts';
import { MultiSelect } from '@/components/multi-select';
import { queryClient, trpc } from '@/lib/trpc';

const NEW_VALUE = '__new';

export function TagAssetsModal({ assetIds, drop, batchId = null, fileCount, facets = true, initialAnalyse = true, onClose }: {
  assetIds: string[];
  /** What the Upload screen's Analyse after upload says for this drop. */
  initialAnalyse?: boolean;
  drop?: boolean;
  /** The drop this modal answers for, when it holds several video files; null otherwise. */
  batchId?: string | null;
  fileCount?: number;
  /** False on the creator page: only the project question, no facets. */
  facets?: boolean;
  onClose: () => void;
}) {
  const fields = useQuery(trpc.facets.uploadFields.queryOptions(undefined, { enabled: facets }));
  const current = useQuery(trpc.tags.assetFacets.queryOptions({ assetIds }, { enabled: facets }));
  const [grouping, setGrouping] = useState<UploadGrouping>('separate');
  // On by default: a drop the user says nothing about is the drop they want analysed.
  const [autoAnalyse, setAutoAnalyse] = useState(initialAnalyse);
  const decide = useMutation(trpc.upload.decide.mutationOptions());
  const decidedRef = useRef(false);
  /** The drop's answer goes once, whichever way the modal closes; the table refreshes on it. */
  const settle = async (): Promise<void> => {
    if (!batchId || decidedRef.current) return;
    decidedRef.current = true;
    try {
      await decide.mutateAsync({ batchId, grouping, autoAnalyse: analyseRef.current });
    } finally {
      void queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() });
    }
  };
  const close = () => {
    void settle();
    onClose();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const groupingRef = useRef(grouping);
  groupingRef.current = grouping;
  const analyseRef = useRef(autoAnalyse);
  analyseRef.current = autoAnalyse;
  useEffect(() => () => {
    // Unmounted without a click (navigation, a re-render that dropped it): the answer still goes.
    if (batchId && !decidedRef.current) {
      decidedRef.current = true;
      void decide.mutateAsync({ batchId, grouping: groupingRef.current, autoAnalyse: analyseRef.current }).finally(() => void queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() }));
    }
  }, []);
  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  const [newValue, setNewValue] = useState<Record<string, string>>({});
  const [seeded, setSeeded] = useState(false);
  const save = useMutation(trpc.tags.tagAssets.mutationOptions({
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.tags.pathKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.assets.pathKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.search.pathKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.tenant.queues.queryKey() }),
      ]);
      onClose();
    },
  }));

  // The dropdowns open on what every clip shares now; from then on they are the user's.
  const initial = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(current.data?.values ?? {})) out[key] = [...values];
    return out;
  }, [current.data]);
  useEffect(() => {
    if (seeded || !current.data) return;
    setChosen({ ...initial });
    setSeeded(true);
  }, [current.data, initial, seeded]);

  /** The picked values per field, a select's "Add new" text included. */
  const picked = (): Record<string, string[]> =>
    Object.fromEntries(Object.entries(chosen).map(([key, list]) => [key, list.map((v) => (v === NEW_VALUE ? (newValue[key] ?? '').trim() : v))]));
  const changes = seeded ? facetChanges(initial, picked()) : [];
  const names = current.data?.assets.map((a) => a.name) ?? [];
  const count = fileCount ?? assetIds.length;
  const heading = !facets ? 'Files in this drop' : drop ? 'Tag this drop' : 'Tag this clip';
  const scope = count > 1
    ? grouping === 'project' ? `this project of ${count} files` : `every clip in this drop of ${count} files`
    : names[0] ? `"${names[0]}" and its subclips` : 'this clip and its subclips';
  /** Save: the drop's answer first (a project folds onto its first clip), then the tags onto what remains. */
  const saveAll = async (): Promise<void> => {
    await settle();
    save.mutate({ assetIds: grouping === 'project' ? [assetIds[0]!] : assetIds, changes });
  };

  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={close} />
      <div className="modal tag-batch-modal" role="dialog" aria-label={heading}>
        <h2>{heading}</h2>
        {batchId && (
          <div className="tag-batch-project">
            <span className="label">These {count} files are one project</span>
            <div className="seg" role="radiogroup" aria-label="One project or separate clips">
              <button type="button" role="radio" aria-checked={grouping === 'project'} className={grouping === 'project' ? 'on' : ''} onClick={() => setGrouping('project')}>Yes, one project</button>
              <button type="button" role="radio" aria-checked={grouping === 'separate'} className={grouping === 'separate' ? 'on' : ''} onClick={() => setGrouping('separate')}>No, separate clips</button>
            </div>
            <span className="sub">A project plays as one clip: one row, one timeline, one set of highlights and tags. The files stay exactly as they arrived. Closing this without answering keeps them separate.</span>
            <label className="row tag-batch-analyse">
              <input type="checkbox" checked={autoAnalyse} onChange={(e) => setAutoAnalyse(e.target.checked)} />
              <span className="label">Analyse now</span>
              <span className="sub">Off brings them in with a quick preview, ready for Composer Fast and for the Analyse button whenever you want it.</span>
            </label>
          </div>
        )}
        {facets && <span className="sub">All optional. Anything you change applies to {scope}; a new name joins its dropdown for next time, so nobody ever types it twice. Fields you leave alone keep whatever tags they already have.</span>}
        {facets && <div className="tag-batch-grid">
          {seeded && fields.data?.map((f) => {
            if (f.multiple) {
              return (
                <div key={f.key} className="tag-batch-field">
                  <span className="label">{f.label}</span>
                  <MultiSelect label={f.label} options={f.values} value={chosen[f.key] ?? []}
                    onChange={(next) => setChosen((c) => ({ ...c, [f.key]: next }))} />
                </div>
              );
            }
            const shown = initial[f.key]?.[0];
            const also = (initial[f.key] ?? []).slice(1);
            // A value the clips already carry but the brand no longer offers still has to be showable.
            const options = shown && !f.values.some((v) => v.toLowerCase() === shown.toLowerCase()) ? [...f.values, shown] : f.values;
            return (
              <label key={f.key} className="tag-batch-field">
                <span className="label">{f.label}</span>
                <select value={chosen[f.key]?.[0] ?? ''} aria-label={f.label}
                  onChange={(e) => setChosen((c) => ({ ...c, [f.key]: e.target.value ? [e.target.value] : [] }))}>
                  <option value="">No tag</option>
                  {groupFacetValues(options).map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.values.map((v) => <option key={v} value={v}>{v}</option>)}
                    </optgroup>
                  ))}
                  <option value={NEW_VALUE}>Add new…</option>
                </select>
                {chosen[f.key]?.[0] === NEW_VALUE && (
                  <input autoFocus value={newValue[f.key] ?? ''} placeholder={`New ${f.label.toLowerCase()}`} maxLength={120}
                    aria-label={`New ${f.label}`}
                    onChange={(e) => setNewValue((n) => ({ ...n, [f.key]: e.target.value }))} />
                )}
                {also.length > 0 && <span className="sub">Also tagged {also.join(', ')}; changing this field replaces them too</span>}
              </label>
            );
          })}
          {(fields.isLoading || current.isLoading) && <div className="empty"><span className="spinner" aria-label="Loading" /></div>}
        </div>}
        {changes.length > 0 && (
          <span className="sub tag-batch-note">Auto-tagging: these tags apply to every subclip as well, and to a clip still analysing as soon as its analysis lands.</span>
        )}
        {(fields.isError || current.isError || save.isError || decide.isError) && (
          <div className="error" role="alert">{fields.error?.message ?? current.error?.message ?? save.error?.message ?? decide.error?.message}</div>
        )}
        <div className="row between">
          <button type="button" className="btn quiet" onClick={close}>Cancel</button>
          <button type="button" className="btn primary" disabled={save.isPending || decide.isPending}
            onClick={() => (changes.length ? void saveAll() : close())}>
            {save.isPending ? 'Saving…' : changes.length ? 'Save tags' : !facets ? 'Done' : drop ? 'Tag later' : 'Done'}
          </button>
        </div>
      </div>
    </>
  );
}
